import { useEffect, useState } from 'react';

const loadedFonts = new Set<string>();
// Per-font Promise that resolves when the stylesheet containing that font has loaded
const fontLoadPromises = new Map<string, Promise<void>>();

// CJK and Devanagari fonts don't have an italic axis in Google Fonts —
// requesting ital, wght would return a 400 error. These must use wght@400;700 only.
const NON_LATIN_FONTS = new Set([
  'Noto Serif JP', 'Noto Sans JP', 'Shippori Mincho', 'Zen Old Mincho',
  'M PLUS Rounded 1c', 'Zen Kaku Gothic New',
  'Noto Serif SC', 'Noto Sans SC', 'ZCOOL XiaoWei', 'Ma Shan Zheng',
  'Tiro Devanagari Hindi', 'Laila', 'Baloo 2', 'Hind', 'Noto Sans Devanagari', 'Mukta',
]);

function injectFontLink(families: string): Promise<void> {
  const link = document.createElement('link');
  link.rel = 'stylesheet';
  link.href = `https://fonts.googleapis.com/css2?${families}&display=swap`;
  return new Promise<void>(resolve => {
    link.onload  = () => resolve();
    link.onerror = () => resolve(); // don't block on network error — fallback font is fine
    document.head.appendChild(link);
  });
}

/**
 * Dynamically load Google Fonts by injecting a <link> tag into <head>.
 * Returns a Promise that resolves when the stylesheet has finished loading so
 * callers can safely call document.fonts.load() immediately after awaiting this.
 * CJK and Devanagari fonts are automatically requested with the correct axis spec.
 */
export function loadGoogleFonts(fonts: string[]): Promise<void> {
  const newFonts = fonts.filter(f => f && !loadedFonts.has(f));

  if (!newFonts.length) {
    // All fonts already injected — return existing promises so callers still wait
    const pending = fonts
      .filter(f => f)
      .map(f => fontLoadPromises.get(f))
      .filter((p): p is Promise<void> => p !== undefined);
    return pending.length ? Promise.all(pending).then(() => {}) : Promise.resolve();
  }

  newFonts.forEach(f => loadedFonts.add(f));

  const latinFonts    = newFonts.filter(f => !NON_LATIN_FONTS.has(f));
  const nonLatinFonts = newFonts.filter(f =>  NON_LATIN_FONTS.has(f));
  const requests: Promise<void>[] = [];

  if (latinFonts.length) {
    const families = latinFonts
      .map(f => `family=${encodeURIComponent(f)}:ital,wght@0,300;0,400;0,500;0,600;0,700;1,400`)
      .join('&');
    const p = injectFontLink(families);
    latinFonts.forEach(f => fontLoadPromises.set(f, p));
    requests.push(p);
  }

  if (nonLatinFonts.length) {
    const families = nonLatinFonts
      .map(f => `family=${encodeURIComponent(f)}:wght@400;700`)
      .join('&');
    const p = injectFontLink(families);
    nonLatinFonts.forEach(f => fontLoadPromises.set(f, p));
    requests.push(p);
  }

  return Promise.all(requests).then(() => {});
}

/**
 * React hook to load Google Fonts and force a re-render once they're ready.
 */
export function useGoogleFonts(fonts: string[]) {
  const [, setReady] = useState(0);
  const key = fonts.join(',');

  useEffect(() => {
    if (!fonts.length || fonts.every(f => !f)) return;
    let cancelled = false;
    loadGoogleFonts(fonts).then(() => {
      if (!cancelled) setReady(n => n + 1);
    });
    return () => { cancelled = true; };
  }, [key]);
}

/**
 * Script/calligraphy fonts that need larger sizes to remain legible.
 * These are only readable at larger point sizes — boost when used as heading font.
 */
const SCRIPT_FONTS = new Set([
  'Dancing Script',
  'Great Vibes',
  'Sacramento',
  'Alex Brush',
  'Pinyon Script',
  'Tangerine',
  'Petit Formal Script',
]);

export function isScriptFont(fontName: string): boolean {
  return SCRIPT_FONTS.has(fontName);
}

/**
 * Ornate/display fonts that render visually smaller than their point size implies.
 * Includes script calligraphy + decorative display faces — bump size in the hero.
 */
const ORNATE_FONTS = new Set([
  ...SCRIPT_FONTS,
  'Ballet',
  'Forum',
  'Italiana',
  'Antic Didone',
]);

export function isOrnateFont(fontName: string): boolean {
  return ORNATE_FONTS.has(fontName);
}

export interface LocaleFontOption {
  value: string;
  label: string;
}

export interface LocaleFontSet {
  heading: LocaleFontOption[];
  body: LocaleFontOption[];
}

/** Locale-specific font options for non-Latin scripts (CJK, Devanagari). */
export const LOCALE_FONTS: Record<string, LocaleFontSet> = {
  ja: {
    heading: [
      { value: 'Noto Serif JP',    label: 'Noto Serif JP' },
      { value: 'Shippori Mincho',  label: 'Shippori Mincho' },
      { value: 'Zen Old Mincho',   label: 'Zen Old Mincho' },
    ],
    body: [
      { value: 'Noto Sans JP',         label: 'Noto Sans JP' },
      { value: 'M PLUS Rounded 1c',    label: 'M PLUS Rounded 1c' },
      { value: 'Zen Kaku Gothic New',  label: 'Zen Kaku Gothic New' },
    ],
  },
  'zh-CN': {
    heading: [
      { value: 'Noto Serif SC',  label: 'Noto Serif SC' },
      { value: 'ZCOOL XiaoWei', label: 'ZCOOL XiaoWei' },
      { value: 'Ma Shan Zheng', label: 'Ma Shan Zheng' },
    ],
    body: [
      { value: 'Noto Sans SC', label: 'Noto Sans SC' },
    ],
  },
  hi: {
    heading: [
      { value: 'Tiro Devanagari Hindi', label: 'Tiro Devanagari Hindi' },
      { value: 'Laila',                 label: 'Laila' },
      { value: 'Baloo 2',              label: 'Baloo 2' },
    ],
    body: [
      { value: 'Hind',                  label: 'Hind' },
      { value: 'Noto Sans Devanagari',  label: 'Noto Sans Devanagari' },
      { value: 'Mukta',                 label: 'Mukta' },
    ],
  },
};

/** Returns the locale-specific font set if the locale uses a non-Latin script, otherwise null. */
export function getLocaleFontSet(locale: string): LocaleFontSet | null {
  return LOCALE_FONTS[locale] ?? null;
}

export interface LocaleFontPairing {
  id: string;
  name: string;
  heading: string;
  body: string;
}

/** Curated font pairings for non-Latin locales, shown in the font pairing picker. */
export const LOCALE_FONT_PAIRINGS: Record<string, LocaleFontPairing[]> = {
  ja: [
    { id: 'ja-traditional',  name: '伝統的', heading: 'Noto Serif JP',   body: 'Noto Sans JP' },
    { id: 'ja-elegant',      name: '優雅',   heading: 'Shippori Mincho', body: 'M PLUS Rounded 1c' },
    { id: 'ja-contemporary', name: '現代的', heading: 'Zen Old Mincho',  body: 'Zen Kaku Gothic New' },
  ],
  'zh-CN': [
    { id: 'zh-refined',      name: '典雅',   heading: 'Noto Serif SC',  body: 'Noto Sans SC' },
    { id: 'zh-traditional',  name: '传统',   heading: 'ZCOOL XiaoWei',  body: 'Noto Sans SC' },
    { id: 'zh-calligraphic', name: '书法',   heading: 'Ma Shan Zheng',  body: 'Noto Sans SC' },
  ],
  hi: [
    { id: 'hi-traditional',  name: 'पारंपरिक', heading: 'Tiro Devanagari Hindi', body: 'Hind' },
    { id: 'hi-elegant',      name: 'सुंदर',    heading: 'Laila',                 body: 'Noto Sans Devanagari' },
    { id: 'hi-modern',       name: 'आधुनिक',  heading: 'Baloo 2',               body: 'Mukta' },
  ],
};

/** Sample preview text shown in font pairing cards for non-Latin locales. */
export const LOCALE_PREVIEW_TEXT: Record<string, { heading: string; body: string }> = {
  ja:    { heading: '花子 ＆ 太郎', body: 'ご家族の皆様とともに' },
  'zh-CN': { heading: '小明 & 小红', body: '与家人共度美好时光' },
  hi:    { heading: 'आर्या और रवि', body: 'परिवार के साथ' },
};

/** All available invite font names (for lazy-loading in font picker). */
export const ALL_INVITE_FONTS = [
  // Heading fonts (serifs, scripts, display)
  'Cormorant Garamond',
  'Playfair Display',
  'Lora',
  'Dancing Script',
  'EB Garamond',
  'Libre Baskerville',
  'Crimson Text',
  'DM Serif Display',
  'Bodoni Moda',
  'Cinzel',
  'Marcellus',
  'Great Vibes',
  'Sacramento',
  'Sorts Mill Goudy',
  'Antic Didone',
  'Italiana',
  'Forum',
  'Alex Brush',
  'Pinyon Script',
  'Tangerine',
  'Ballet',
  'Petit Formal Script',
  'Nanum Myeongjo',
  // Japanese
  'Noto Serif JP',
  'Shippori Mincho',
  'Zen Old Mincho',
  // Chinese Simplified
  'Noto Serif SC',
  'ZCOOL XiaoWei',
  'Ma Shan Zheng',
  // Hindi / Devanagari
  'Tiro Devanagari Hindi',
  'Laila',
  'Baloo 2',
  // Body fonts (sans-serifs + body serifs)
  'Quicksand',
  'Montserrat',
  'Jost',
  'Lato',
  'Open Sans',
  'Inter',
  'Raleway',
  'Poppins',
  'Work Sans',
  'DM Sans',
  'Josefin Sans',
  'Nunito',
  'Hanken Grotesk',
  'Crimson Pro',
  'Tenor Sans',
  // Japanese body
  'Noto Sans JP',
  'M PLUS Rounded 1c',
  'Zen Kaku Gothic New',
  // Chinese Simplified body
  'Noto Sans SC',
  // Hindi / Devanagari body
  'Hind',
  'Noto Sans Devanagari',
  'Mukta',
];
